import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Arrow } from '@commercial-website/icons';
import { useState } from 'react';
import { Band } from '../../atoms/band/band';
import { IconButton } from '../../atoms/icon-button/icon-button';
import { cn } from '../../utils/cn';
// `neutral` is the light background -> pairs with the light `IconButton` variant; `black` is the
// full-bleed dark background -> pairs with `grey`.
//
// `white` is an exception: below `sm:` the arrows sit in normal flow on the page's own surface
// (outside Band's background), where the `white` IconButton (bg-static) would blend in — so
// mobile uses `grey` there instead, and `arrowSmClassesForVariant` below reinstates the `white`
// look once the arrows pin over Band's background at `sm:`+.
const iconButtonVariantByVariant = {
    white: 'grey',
    neutral: 'white',
    none: 'white',
    pink: 'darkGrey',
    black: 'grey',
};
// Re-applies `IconButton`'s own `white` variant recipe (see `variantClasses.white` in
// `icon-button-styles.ts`) at `sm:`+ only, for the `white` band variant's mobile->desktop
// IconButton variant swap described above.
const arrowSmClassesForVariant = {
    white: 'sm:bg-static sm:text-static sm:hover:bg-static-loud',
};
// No `text-static-inverse-quiet` token exists for a muted tone on a dark surface —
// `text-interactive-inverse-quiet` (neutral-300) is the closest existing Telescope token and is
// used for the role line on `black` only. `neutral`'s role line keeps inheriting Band's own
// `text-neutral` color untouched.
const roleClassByVariant = {
    neutral: undefined,
    white: undefined,
    none: undefined,
    pink: undefined,
    black: 'text-interactive-inverse-quiet',
};
export const QuoteCarousel = ({ items, variant = 'white', ariaLabels, className, }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    if (items.length === 0)
        return null;
    const hasMultipleItems = items.length > 1;
    // Clamp so a shorter `items` array replacing the current one while mounted (e.g. a Storybook
    // control edit, or a CMS-driven re-render) can never index past the end.
    const currentClampedIndex = Math.min(currentIndex, items.length - 1);
    const goTo = (direction) => {
        // Normalize the stored index against the *current* `items` length before applying the
        // direction — otherwise a stale `index` left over from a longer `items` array (e.g. a
        // Storybook control edit, or a CMS-driven re-render shrinking the list) can resolve to the
        // same clamped index the render already showed, making the first click after a shrink
        // appear to do nothing.
        setCurrentIndex((index) => {
            const normalizedIndex = Math.min(index, items.length - 1);
            return (normalizedIndex + direction + items.length) % items.length;
        });
    };
    const quoteBlock = (_jsx("div", { "aria-live": "polite", "aria-atomic": "true", className: "grid max-w-180 grid-cols-1", children: items.map((item, index) => {
            const isCurrent = index === currentClampedIndex;
            return (_jsxs("div", { "aria-hidden": isCurrent ? undefined : true, "aria-current": isCurrent ? true : undefined, className: cn('col-start-1 row-start-1 flex flex-col items-center justify-center gap-4 transition-opacity duration-300 motion-reduce:transition-none motion-reduce:duration-0', isCurrent ? 'opacity-100' : 'pointer-events-none opacity-0'), children: [_jsx("blockquote", { className: "text-heading-xl lg:text-heading-3xl m-0 break-normal wrap-anywhere", children: item.quote }), _jsxs("p", { className: "text-body-s m-0 flex flex-col items-center break-normal wrap-anywhere", children: [_jsx("span", { className: "text-label-m-strong", children: item.attribution.name }), _jsx("span", { className: cn(roleClassByVariant[variant]), children: item.attribution.role })] })] }, `${item.attribution.name}-${index}`));
        }) }));
    const arrows = hasMultipleItems && (_jsxs("div", { className: cn('flex gap-2 px-2 py-4 sm:contents sm:px-6', 
        // At sm+ the arrows dissolve to `contents` and pin over Band's background. On the
        // black variant that background is dark but the arrows render outside Band, so they
        // don't inherit its `focus-surface-inverse` — opt them in here (custom properties
        // still cascade through `sm:contents`) so their focus ring is legible. Below sm the
        // arrows sit under the band on the page's own surface, so the default ring stays.
        variant === 'black' && 'sm:focus-surface-inverse'), children: [_jsx(IconButton, { shape: "round", variant: iconButtonVariantByVariant[variant], "aria-label": ariaLabels?.previous ?? 'Previous slide', icon: _jsx(Arrow, { className: "rotate-180" }), onClick: () => goTo(-1), className: cn('cursor-pointer sm:absolute sm:top-1/2 sm:left-4 sm:-translate-y-1/2', arrowSmClassesForVariant[variant]) }), _jsx(IconButton, { shape: "round", variant: iconButtonVariantByVariant[variant], "aria-label": ariaLabels?.next ?? 'Next slide', icon: _jsx(Arrow, {}), onClick: () => goTo(1), className: cn('cursor-pointer sm:absolute sm:top-1/2 sm:right-4 sm:-translate-y-1/2', arrowSmClassesForVariant[variant]) })] }));
    // No `Container` composed here — `Band` is `QuoteCarousel`'s own root at whatever width the
    // consuming page renders it (full-bleed if placed directly, inset if the consumer wraps it in
    // `Container` itself). Padding (`px-6`) provides the inner gutter directly instead. The outer
    // wrapper below renders no visible box of its own — it exists only to give `arrows` an
    // `sm:absolute` positioning root that matches Band's box at `sm:`+.
    return (_jsxs("div", { className: "sm:relative", children: [_jsx(Band, { variant: variant, as: "section", className: cn(
                // `sm:px-16` (64px) clears the `sm:absolute` arrows' 16-48px zone (`sm:left-4`/
                // `sm:right-4` inset + the IconButton's own `h-8 w-8`) with room to spare —
                // `px-6` (24px) alone left a multi-line/max-width quote sitting under the
                // buttons at viewports just above the `sm` breakpoint.
                'flex flex-col items-center gap-8 px-6 py-16 text-center sm:px-16 sm:py-20', className), children: quoteBlock }), arrows] }));
};
